import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Radio, Button, message, Space, Typography, Alert, Statistic, Row, Col } from 'antd';
import { MailOutlined, PhoneOutlined, CopyOutlined, CheckCircleOutlined, DollarOutlined, MessageOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { copyToClipboard } from '../../utils/clipboard';

const { Text, Title } = Typography;
const { confirm } = Modal;

interface SendWorkOrderModalProps {
  visible: boolean;
  onClose: () => void;
  workOrderId: number;
  workOrderNumber: string;
  customerEmail?: string;
  customerPhone?: string;
  onSent?: () => void;
}

const SendWorkOrderModal: React.FC<SendWorkOrderModalProps> = ({
  visible,
  onClose,
  workOrderId,
  workOrderNumber,
  customerEmail,
  customerPhone,
  onSent,
}) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string>('');
  const [method, setMethod] = useState<'email' | 'sms' | 'both'>('email');
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [estimatingCost, setEstimatingCost] = useState(false);

  useEffect(() => {
    if (method === 'sms' || method === 'both') {
      estimateSMSCost();
    } else {
      setEstimatedCost(0);
    }
  }, [method]);

  const estimateSMSCost = async () => {
    try {
      setEstimatingCost(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      // Estimate a typical message length for work orders (~180 characters)
      const estimatedMessage = `CNF Auto LLC: Additional repairs needed for your vehicle (Work Order #${workOrderNumber}). Please review and approve: https://example.com/wo/xxxxx`;

      const response = await axios.post(
        `${API_BASE_URL}/admin/sms/estimate-cost`,
        { message: estimatedMessage },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const costPerMessage = response.data.estimatedCost || 0.0075;
      const recipientCount = method === 'both' ? 1 : 1; // In this case, always 1 customer
      const totalCost = method === 'both' ? costPerMessage : costPerMessage;

      setEstimatedCost(totalCost);
    } catch (error) {
      console.error('Error estimating SMS cost:', error);
      // Default to $0.0075 per message if estimation fails
      setEstimatedCost(0.0075);
    } finally {
      setEstimatingCost(false);
    }
  };

  const showSendConfirmation = (values: any) => {
    const methodText = method === 'both' ? 'Email & SMS' : method === 'email' ? 'Email' : 'SMS';
    const costText = method !== 'email' ? ` (Cost: $${estimatedCost.toFixed(4)})` : '';

    confirm({
      title: `Send Work Order via ${methodText}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Work Order <Text strong>#{workOrderNumber}</Text> will be sent to:
          </Text>
          {(method === 'email' || method === 'both') && (
            <Text>
              <MailOutlined /> Email: {values.to}
            </Text>
          )}
          {(method === 'sms' || method === 'both') && (
            <Text>
              <PhoneOutlined /> SMS: {values.to}
            </Text>
          )}
          {method !== 'email' && (
            <Alert
              message={`Estimated Cost: $${estimatedCost.toFixed(4)}`}
              type="info"
              showIcon
              icon={<DollarOutlined />}
              style={{ marginTop: 8 }}
            />
          )}
        </Space>
      ),
      okText: 'Send Now',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => handleSend(values),
    });
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Show confirmation before sending
      if (method === 'sms' || method === 'both') {
        showSendConfirmation(values);
      } else {
        // For email only, send directly
        await handleSend(values);
      }
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleSend = async (values: any) => {
    try {
      setSending(true);

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tokens = localStorage.getItem('cnf_auth_tokens');
      let accessToken = '';

      if (tokens) {
        const parsed = JSON.parse(tokens);
        accessToken = parsed.accessToken;
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/work-orders/${workOrderId}/send`,
        {
          method: values.method,
          to: values.to,
          shopName: values.shopName || 'CNF Auto LLC',
          shopPhone: values.shopPhone,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setPublicUrl(response.data.publicUrl);
      setSent(true);

      const methodText = method === 'both' ? 'email and SMS' : method;
      message.success(`Work order sent successfully via ${methodText}!`);

      if (onSent) {
        onSent();
      }
    } catch (error: any) {
      console.error('Error sending work order:', error);
      message.error(error.response?.data?.error || 'Failed to send work order');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSent(false);
    setPublicUrl('');
    setMethod('email');
    setEstimatedCost(0);
    onClose();
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(publicUrl);
    if (success) {
      message.success('Link copied to clipboard!');
    } else {
      message.error('Failed to copy link');
    }
  };

  const handleMethodChange = (e: any) => {
    const newMethod = e.target.value;
    setMethod(newMethod);

    // Update the "to" field based on method
    if (newMethod === 'email') {
      form.setFieldsValue({ to: customerEmail });
    } else if (newMethod === 'sms') {
      form.setFieldsValue({ to: customerPhone });
    } else if (newMethod === 'both') {
      // For both, we'll need both email and phone
      form.setFieldsValue({ to: customerEmail });
    }
  };

  return (
    <Modal
      title={
        <Space>
          {sent ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} /> : <MailOutlined />}
          <span>{sent ? 'Work Order Sent!' : `Send Work Order #${workOrderNumber}`}</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={
        sent ? [
          <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyLink}>
            Copy Link
          </Button>,
          <Button key="done" type="primary" onClick={handleClose}>
            Done
          </Button>,
        ] : [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sending}
            onClick={handleFormSubmit}
            icon={method === 'email' ? <MailOutlined /> : method === 'sms' ? <PhoneOutlined /> : <MessageOutlined />}
          >
            {method === 'both' ? 'Send Both' : `Send ${method === 'email' ? 'Email' : 'SMS'}`}
          </Button>,
        ]
      }
      width={600}
      centered
    >
      {sent ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Work order sent successfully!"
            description={`The customer will receive a link to view and approve the additional repairs.`}
            type="success"
            showIcon
          />

          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Public Link:
            </Text>
            <Input
              value={publicUrl}
              readOnly
              addonAfter={
                <CopyOutlined
                  style={{ cursor: 'pointer' }}
                  onClick={handleCopyLink}
                />
              }
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
              You can also share this link manually with the customer
            </Text>
          </div>
        </Space>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            method: 'email',
            to: method === 'email' ? customerEmail : customerPhone,
            shopName: 'CNF Auto LLC',
          }}
        >
          <Form.Item
            label="Send Method"
            name="method"
            rules={[{ required: true, message: 'Please select a send method' }]}
          >
            <Radio.Group onChange={handleMethodChange}>
              <Radio.Button value="email">
                <MailOutlined /> Email
              </Radio.Button>
              <Radio.Button value="sms">
                <PhoneOutlined /> SMS
              </Radio.Button>
              <Radio.Button value="both">
                <MessageOutlined /> Both
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {(method === 'sms' || method === 'both') && estimatedCost > 0 && (
            <Alert
              message={
                <Space>
                  <DollarOutlined />
                  <Text strong>Estimated Cost: ${estimatedCost.toFixed(4)}</Text>
                </Space>
              }
              description="Cost per SMS message. Final cost may vary based on message length."
              type="info"
              showIcon={false}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            label={method === 'email' ? 'Recipient Email' : method === 'sms' ? 'Recipient Phone' : 'Recipient Email & Phone'}
            name="to"
            rules={[
              { required: true, message: `Please enter the recipient's ${method === 'email' ? 'email' : method === 'sms' ? 'phone number' : 'email'}` },
              method === 'email' || method === 'both'
                ? { type: 'email', message: 'Please enter a valid email address' }
                : {},
            ]}
          >
            <Input
              placeholder={method === 'email' || method === 'both' ? 'customer@example.com' : '+15551234567'}
              prefix={method === 'email' ? <MailOutlined /> : method === 'sms' ? <PhoneOutlined /> : <MailOutlined />}
              size="large"
            />
          </Form.Item>

          {method === 'both' && (
            <Form.Item
              label="Recipient Phone"
              name="phone"
              rules={[
                { required: true, message: 'Please enter the recipient phone number' },
                {
                  pattern: /^\+?[1-9]\d{1,14}$/,
                  message: 'Please enter a valid phone number in E.164 format (+15551234567)',
                },
              ]}
            >
              <Input
                placeholder="+15551234567"
                prefix={<PhoneOutlined />}
                size="large"
              />
            </Form.Item>
          )}

          {method === 'sms' && (
            <Alert
              message="Phone Number Format"
              description="Please enter phone number in E.164 format (e.g., +15551234567)"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            label="Shop Name"
            name="shopName"
            tooltip={`This will appear in the ${method === 'both' ? 'email and SMS' : method}`}
          >
            <Input placeholder="CNF Auto LLC" size="large" />
          </Form.Item>

          <Form.Item
            label="Shop Phone (Optional)"
            name="shopPhone"
            tooltip="Will be included for customer contact"
          >
            <Input placeholder="(555) 123-4567" size="large" />
          </Form.Item>

          <Alert
            message="What happens next?"
            description={
              method === 'email'
                ? 'The customer will receive an email with a link to view and approve the additional repairs online. They can sign digitally on any device.'
                : method === 'sms'
                ? 'The customer will receive an SMS with a link to view and approve the additional repairs online. Standard SMS rates apply (~$0.0075/message).'
                : 'The customer will receive both an email and SMS with a link to view and approve the additional repairs online.'
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Form>
      )}
    </Modal>
  );
};

export default SendWorkOrderModal;
